import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, Clock, FileText, Search } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Faq } from '../components/Faq';
import { INDICATIVE_RATE_PCT } from '../lib/finance';

/**
 * /pre-approval — car finance pre-approval, explained.
 *
 * WHY THIS PAGE EXISTS: "car loan pre approval" and its variants run around 1,050 searches a month
 * in NZ at difficulty 0–14, and the SERP is soft — one competitor holds #2 with a real page, the
 * incumbent lender sits at **#14**, and half of page one is blog posts and dealer enquiry forms.
 *
 * ⚠️ THE LINE THIS PAGE MUST NOT CROSS, and the reason it is written the way it is:
 *
 * **Levr's two-minute check is NOT a pre-approval.** Our own disclaimer says exactly that — it is an
 * indicative eligibility estimate, no lender has seen it, and it binds nobody. A page that ranked
 * for "pre-approval" by implying we grant one would be selling something we do not have, on a
 * consumer-credit site, to people about to make a large purchase on the strength of it.
 *
 * So the page explains what pre-approval genuinely is, who actually issues one (a lender), and
 * positions our check honestly as the step BEFORE it — worth doing precisely because it costs
 * nothing and touches no credit file. That framing is not a compromise for compliance reasons; it
 * is also the better page. The queries here are informational, which is why blog posts are
 * outranking lenders on them.
 */

const STEPS = [
  {
    icon: Search,
    title: 'Check what you can realistically borrow',
    body: (
      <>
        Two minutes, no credit check, no obligation. You get an indicative amount and repayment,
        and a read on how likely approval is. Nothing here touches your credit file, and no lender
        has seen anything yet.
      </>
    ),
  },
  {
    icon: FileText,
    title: 'A specialist works out where it fits',
    body: (
      <>
        If you want to go further, one of our people reviews what you have told us and works out
        which lenders suit your situation — your credit history, your income and commitments, and
        the vehicle you have in mind.
      </>
    ),
  },
  {
    icon: ShieldCheck,
    title: 'The lender issues the pre-approval',
    body: (
      <>
        Pre-approval comes from the lender, not from us. They assess you properly — which normally
        does involve a credit check — and confirm a maximum amount, usually valid for a set period.
        That is the document you can shop with.
      </>
    ),
  },
];

export default function PreApproval() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="pt-24 pb-12 md:pt-32 md:pb-16 bg-gradient-to-br from-black via-gray-900 to-black">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-[#00D47E]/20 text-[#00D47E] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Clock className="w-4 h-4" />
              Know your number before you shop
            </div>
            <h1 className="font-outfit text-3xl md:text-5xl font-bold text-white mb-5 leading-tight">
              Car finance pre-approval in New Zealand
            </h1>
            <p className="text-gray-300 text-lg leading-relaxed">
              Walking onto a lot without knowing what you can borrow puts you in the weakest position
              in the room. Here is what pre-approval actually is, what it is worth, and how to get
              there — starting with a two-minute check that costs nothing and leaves no mark on your
              credit file.
            </p>
            <Button
              data-testid="preapproval-hero-cta"
              onClick={() => navigate('/get-started')}
              className="mt-8 bg-[#00D47E] text-black border-2 border-[#00D47E] hover:bg-transparent hover:text-white hover:border-white rounded-full px-8 py-6 text-lg font-medium transition-all"
            >
              Check what I could borrow
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* ---- What it is ----------------------------------------------------------------------- */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-outfit text-2xl md:text-3xl font-bold text-black mb-4">
            What pre-approval actually means
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            A pre-approval is a lender telling you, in advance and in writing, roughly how much they
            are prepared to lend you and on what terms — before you have chosen a car. It is
            conditional: it normally still depends on the vehicle you end up buying and on the
            details you gave holding true. It usually lasts somewhere between a fortnight and a
            couple of months.
          </p>
          <p className="text-gray-700 leading-relaxed">
            What it buys you is a budget you can trust and a stronger position when you negotiate.
            You are no longer asking a seller what you can afford; you already know, and you are not
            depending on whatever finance happens to be offered at the desk.
          </p>

          <div className="mt-8 border-l-4 border-[#00D47E] bg-gray-50 rounded-r-lg p-6">
            <h3 className="font-outfit font-semibold text-black mb-2">
              What our two-minute check is — and what it is not
            </h3>
            <p className="text-gray-700 leading-relaxed">
              Our eligibility check is <strong>not</strong> a pre-approval, and we would rather say
              so plainly than let you find out later. It is an indicative estimate based only on what
              you tell us: no credit check, no lender involved, and it binds nobody. What it is good
              for is finding out whether the number you have in mind is realistic — before anyone
              runs a credit check on you, and without committing to anything.
            </p>
          </div>
        </div>
      </section>

      {/* ---- How to get there ----------------------------------------------------------------- */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-outfit text-2xl md:text-3xl font-bold text-black mb-10">
            How you get from here to a pre-approval
          </h2>
          <div className="flex flex-col gap-8">
            {STEPS.map(({ icon: Icon, title, body }, i) => (
              <div key={title} className="flex gap-5">
                <div className="shrink-0">
                  <div className="w-11 h-11 rounded-full bg-[#00D47E]/10 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#00D47E]" />
                  </div>
                </div>
                <div>
                  <h3 className="font-outfit text-lg font-semibold text-black mb-1">
                    {i + 1}. {title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---- What to have ready ---------------------------------------------------------------- */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-outfit text-2xl md:text-3xl font-bold text-black mb-4">
            What a lender will want to see
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Our initial check asks for none of this — but when a full application comes, having it to
            hand is the difference between a decision in hours and one in days:
          </p>
          <ul className="flex flex-col gap-3 text-gray-700 leading-relaxed list-disc pl-6">
            <li>Photo ID — a NZ driver licence or passport</li>
            <li>Proof of income, usually recent payslips, or returns if you are self-employed</li>
            <li>Recent bank statements</li>
            <li>An honest account of your existing commitments — other loans, buy-now-pay-later, credit cards, rent or mortgage</li>
            <li>Details of the vehicle, once you have one in mind</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mt-6">
            Be straight about the commitments. Lenders verify against your bank statements anyway,
            and an application that does not match is the fastest route to a decline.
          </p>
        </div>
      </section>

      <Faq
        id="pre-approval-faq"
        title="Questions about pre-approval"
        items={[
          {
            q: 'Does getting pre-approved hurt my credit score?',
            a: (
              <>
                A lender&rsquo;s pre-approval normally involves a credit check, and that is recorded.
                A handful over a short period is normal when someone is shopping for finance; many
                over months can read as a pattern. Our two-minute check runs no credit check at all,
                which is exactly why it is worth doing first — you find out whether your number is
                realistic before anything is recorded anywhere.
              </>
            ),
          },
          {
            q: 'How long does pre-approval last?',
            a: 'Typically two weeks to about three months, depending on the lender. If it lapses before you find a car, it can usually be refreshed — often without starting over.',
          },
          {
            q: 'Am I committed once I am pre-approved?',
            a: 'No. It is an offer to lend, not an obligation to borrow. You can walk away, buy a cheaper car, or not buy at all.',
          },
          {
            q: 'Can I get pre-approved with bad credit?',
            a: (
              <>
                Often, yes — though the rate and the amount will reflect it. Lenders weigh your
                current situation as well as your history, so steady income and honest disclosure
                matter. If money is genuinely tight, talk to{' '}
                <a
                  href="https://www.moneytalks.co.nz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#00D47E] hover:underline"
                >
                  MoneyTalks
                </a>{' '}
                first — free, independent, and they do not sell anything.
              </>
            ),
          },
          {
            q: 'What rate will I get?',
            a: (
              <>
                It depends on your credit history, your income and commitments, the vehicle and the
                term — and it is set by the lender, not by us. Figures on this site are calculated at
                an indicative {INDICATIVE_RATE_PCT}% p.a. and real offers range considerably higher.
                Our <Link to="/calculator" className="text-[#00D47E] hover:underline">calculator</Link>{' '}
                shows what different terms do to the total.
              </>
            ),
          },
          {
            q: 'Is dealer finance different from getting pre-approved first?',
            a: 'Both end in a loan, but the order changes your position. Arranged at the desk, the finance is part of the negotiation and you are comparing one offer. Arriving pre-approved, you already know your budget and your rate, and the car is the only thing left to discuss.',
          },
        ]}
      />

      <section className="py-16 md:py-20 bg-black">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-outfit text-2xl md:text-3xl font-bold text-white mb-4">
            Start with the part that costs nothing
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-xl mx-auto">
            Two minutes, no credit check, no obligation. Find out whether your number is realistic
            before anyone runs a check on you.
          </p>
          <Button
            data-testid="preapproval-footer-cta"
            onClick={() => navigate('/get-started')}
            className="bg-[#00D47E] text-black border-2 border-[#00D47E] hover:bg-transparent hover:text-white hover:border-white rounded-full px-8 py-6 text-lg font-medium transition-all"
          >
            Check my eligibility
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
